import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { programasApi, beneficiosApi, ESTADOS_PROGRAMA } from '../api/programas.js';
import { TrilhoEstado } from '../componentes/transversais/TrilhoEstado.js';
import { ModalTransicao } from '../componentes/transversais/ModalTransicao.js';
import { VazioQueEnsina } from '../componentes/transversais/VazioQueEnsina.js';
import type { TransicaoDisponivel } from '../api/tipos.js';

const COR_TIPO_DEPENDENCIA: Record<string, string> = {
  bloqueante: 'text-status-vermelho',
  informativa: 'text-grafite',
  recurso_compartilhado: 'text-aco',
};

function AbaBeneficios({ programaId }: { programaId: string }) {
  const queryClient = useQueryClient();
  const { data: beneficios } = useQuery({ queryKey: ['programa', programaId, 'beneficios'], queryFn: () => programasApi.beneficios(programaId) });
  const [valores, setValores] = useState<Record<string, string>>({});
  const [decisoes, setDecisoes] = useState<Record<string, string>>({});

  function invalidar() {
    queryClient.invalidateQueries({ queryKey: ['programa', programaId, 'beneficios'] });
  }

  const medir = useMutation({
    mutationFn: ({ id, valor }: { id: string; valor: number }) => beneficiosApi.medir(id, valor),
    onSuccess: invalidar,
  });
  const realizar = useMutation({ mutationFn: (id: string) => beneficiosApi.realizar(id), onSuccess: invalidar });
  const aceitarNaoRealizado = useMutation({
    mutationFn: ({ id, decisao }: { id: string; decisao: string }) => beneficiosApi.aceitarNaoRealizado(id, decisao),
    onSuccess: invalidar,
  });

  if (!beneficios || beneficios.length === 0) {
    return <VazioQueEnsina explicacao="Nenhum benefício declarado ainda. Um benefício nasce automaticamente quando uma demanda é AprovadaMerito." />;
  }

  return (
    <ul className="flex flex-col gap-3">
      {beneficios.map((b) => (
        <li key={b.id} className="rounded-lg border border-borda bg-superficie p-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-ink">{b.descricao}</p>
            <span className="fonte-mono text-xs text-grafite">{b.estado}</span>
          </div>
          <p className="text-xs text-grafite">
            Dono: {b.dono} {b.metaValor !== null && `· meta: ${b.metaValor}`}
          </p>

          {b.estado !== 'Realizado' && b.estado !== 'NaoRealizadoAceito' && (
            <div className="mt-2 flex items-center gap-2">
              <input
                type="number"
                placeholder="Valor medido"
                value={valores[b.id] ?? ''}
                onChange={(e) => setValores((prev) => ({ ...prev, [b.id]: e.target.value }))}
                className="w-28 rounded border border-borda bg-base-100 px-2 py-1 text-xs text-ink"
              />
              <button
                type="button"
                onClick={() => {
                  const valor = Number(valores[b.id]);
                  if (!Number.isNaN(valor)) medir.mutate({ id: b.id, valor });
                }}
                className="rounded border border-aco/40 px-2 py-1 text-xs text-aco hover:bg-aco/5"
              >
                Medir
              </button>
              {b.estado === 'EmMedicao' && (
                <button type="button" onClick={() => realizar.mutate(b.id)} className="rounded bg-aco px-2 py-1 text-xs text-white hover:bg-aco-700">
                  Marcar realizado
                </button>
              )}
            </div>
          )}

          {b.estado === 'EmMedicao' && (
            <div className="mt-2 flex items-center gap-2">
              <input
                placeholder="Motivo do não realizado"
                value={decisoes[b.id] ?? ''}
                onChange={(e) => setDecisoes((prev) => ({ ...prev, [b.id]: e.target.value }))}
                className="flex-1 rounded border border-borda bg-base-100 px-2 py-1 text-xs text-ink"
              />
              <button
                type="button"
                disabled={!decisoes[b.id]}
                onClick={() => aceitarNaoRealizado.mutate({ id: b.id, decisao: decisoes[b.id]! })}
                className="rounded border border-status-vermelho/40 px-2 py-1 text-xs text-status-vermelho hover:bg-status-vermelho/5 disabled:opacity-50"
              >
                Aceitar não realizado
              </button>
            </div>
          )}
        </li>
      ))}
    </ul>
  );
}

// SPEC-UX §4.8 — tela principal é o grafo: nós = projetos, arestas =
// dependências com tipo e data crítica, caminho crítico destacado. Aba
// Benefícios: cada Beneficio com trilho de estado próprio (aqui como
// badge, dado seu ciclo de vida mais simples que projeto/programa), dono,
// meta e medições. Hover com simulação de leitura da cascata (R7.3) fica
// fora — exigiria endpoint dedicado de simulação que o backend ainda não
// expõe (recalcularCascata é serviço puro sem rota HTTP).
export function ProgramaDetalhe() {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const [transicaoAtiva, setTransicaoAtiva] = useState<TransicaoDisponivel | null>(null);
  const [aba, setAba] = useState<'grafo' | 'beneficios'>('grafo');

  const { data: programa } = useQuery({ queryKey: ['programa', id], queryFn: () => programasApi.buscar(id!), enabled: !!id });
  const { data: grafo } = useQuery({ queryKey: ['programa', id, 'grafo'], queryFn: () => programasApi.grafo(id!), enabled: !!id });
  const { data: transicoes } = useQuery({
    queryKey: ['programa', id, 'transicoes'],
    queryFn: () => programasApi.transicoesDisponiveis(id!),
    enabled: !!id,
  });

  const transicionar = useMutation({
    mutationFn: (evento: string) => programasApi.transicionar(id!, evento),
    onSuccess: () => {
      setTransicaoAtiva(null);
      queryClient.invalidateQueries({ queryKey: ['programa', id] });
    },
  });

  if (!programa) return <div className="p-8 text-sm text-grafite">Carregando…</div>;

  const estadosTrilho = ESTADOS_PROGRAMA.map((nome) => ({
    nome,
    atual: nome === programa.estado,
    percorrido: ESTADOS_PROGRAMA.indexOf(nome) < ESTADOS_PROGRAMA.indexOf(programa.estado as never),
  }));

  return (
    <div className="flex flex-col gap-6 p-8">
      <div>
        <h1 className="text-xl font-semibold text-ink">{programa.nome}</h1>
        <p className="text-sm text-grafite">Responsável: {programa.responsavel}</p>
      </div>

      <TrilhoEstado estados={estadosTrilho} transicoesDisponiveis={transicoes ?? []} onSelecionarTransicao={setTransicaoAtiva} />

      {transicaoAtiva && (
        <ModalTransicao
          transicao={transicaoAtiva}
          onFechar={() => setTransicaoAtiva(null)}
          onConfirmar={() => transicionar.mutate(transicaoAtiva.evento)}
          carregando={transicionar.isPending}
        />
      )}

      <div className="flex gap-1 border-b border-borda">
        {(['grafo', 'beneficios'] as const).map((a) => (
          <button
            key={a}
            type="button"
            onClick={() => setAba(a)}
            className={`px-3 py-2 text-sm font-medium capitalize ${aba === a ? 'border-b-2 border-aco text-aco-700' : 'text-grafite'}`}
          >
            {a}
          </button>
        ))}
      </div>

      {aba === 'grafo' && (
        <section>
          {(!grafo || grafo.arestas.length === 0) && (
            <VazioQueEnsina explicacao="Nenhuma dependência declarada entre projetos deste programa ainda." />
          )}
          {grafo && grafo.arestas.length > 0 && (
            <ul className="flex flex-col gap-2">
              {grafo.arestas.map((aresta) => (
                <li key={aresta.id} className="rounded border border-borda bg-superficie px-4 py-3 text-sm">
                  <span className="text-ink">
                    {aresta.projetoOrigemId} → {aresta.projetoDestinoId}
                  </span>
                  <span className={`fonte-mono ml-2 text-xs ${COR_TIPO_DEPENDENCIA[aresta.tipo]}`}>{aresta.tipo}</span>
                  <p className="text-xs text-grafite">
                    {aresta.entregaOrigem} · crítica em {aresta.dataCritica}
                    {grafo.caminhoCritico.includes(aresta.projetoDestinoId) && ' · caminho crítico'}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      {aba === 'beneficios' && <AbaBeneficios programaId={programa.id} />}
    </div>
  );
}
